/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import { useEffect, useMemo, useRef } from 'react';

export type DebounceOptions = {
  // TODO v5: fix boolean naming convention
  // eslint-disable-next-line @typescript-eslint/naming-convention
  leading?: boolean;
  // TODO v5: fix boolean naming convention
  // eslint-disable-next-line @typescript-eslint/naming-convention
  trailing?: boolean;
  maxWait?: number;
};

type GenericFunction = (...args: never[]) => unknown;

type DebouncedState<T extends GenericFunction> = ((
  ...args: Parameters<T>
) => ReturnType<T> | undefined) & {
  cancel: () => void;
  flush: () => ReturnType<T> | undefined;
  isPending: () => boolean;
};

const createDebouncedFunction = <T extends GenericFunction>(
  callback: (...args: Parameters<T>) => ReturnType<T>,
  delay: number,
  options: DebounceOptions,
) => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  let maxWaitTimeoutId: ReturnType<typeof setTimeout> | undefined;
  let lastArgs: Parameters<T> | undefined;
  let result: ReturnType<T> | undefined;

  const isLeading = options.leading ?? false;
  const isTrailing = options.trailing ?? true;
  const maxWait = options.maxWait;

  const clearTimers = () => {
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
      timeoutId = undefined;
    }
    if (maxWaitTimeoutId !== undefined) {
      clearTimeout(maxWaitTimeoutId);
      maxWaitTimeoutId = undefined;
    }
  };

  const invoke = () => {
    if (lastArgs === undefined) {
      return result;
    }
    const args = lastArgs;
    lastArgs = undefined;
    result = callback(...args);
    return result;
  };

  const scheduleTrailing = () => {
    if (!isTrailing) {
      if (isLeading) {
        if (timeoutId !== undefined) {
          clearTimeout(timeoutId);
        }
        timeoutId = setTimeout(() => {
          timeoutId = undefined;
        }, delay);
      }
      return;
    }

    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(() => {
      timeoutId = undefined;

      if (maxWaitTimeoutId !== undefined) {
        clearTimeout(maxWaitTimeoutId);
        maxWaitTimeoutId = undefined;
      }

      invoke();
    }, delay);
  };

  const scheduleMaxWait = () => {
    if (maxWait === undefined || maxWaitTimeoutId !== undefined) {
      return;
    }

    maxWaitTimeoutId = setTimeout(() => {
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
        timeoutId = undefined;
      }

      maxWaitTimeoutId = undefined;
      invoke();
    }, maxWait);
  };

  const debounced = (...args: Parameters<T>) => {
    const isFirstInvocationInBurst =
      timeoutId === undefined && maxWaitTimeoutId === undefined;

    lastArgs = args;

    if (isLeading && isFirstInvocationInBurst) {
      result = callback(...args);
      lastArgs = undefined;
    }

    scheduleTrailing();
    scheduleMaxWait();

    return result;
  };

  const cancel = () => {
    clearTimers();
    lastArgs = undefined;
  };

  const flush = () => {
    clearTimers();
    return invoke();
  };

  const isPending = () =>
    timeoutId !== undefined ||
    maxWaitTimeoutId !== undefined ||
    lastArgs !== undefined;

  return { cancel, debounced, flush, isPending };
};

/**
 * Local override for usehooks-ts useDebounceCallback.
 *
 * This keeps one debounced function instance across rerenders (unless delay/options change)
 * while always calling the latest callback. It avoids resetting debounce timing when
 * inline callbacks are recreated every render.
 */
export function useDebounceCallback<T extends GenericFunction>(
  func: T,
  delay = 500,
  options: DebounceOptions = {},
): DebouncedState<T> {
  const latestCallbackRef = useRef(func);
  const debouncedStateRef =
    useRef<ReturnType<typeof createDebouncedFunction<T>>>(undefined);

  const isLeading = options.leading ?? false;
  const isTrailing = options.trailing ?? true;
  const maxWait = options.maxWait;

  useEffect(() => {
    latestCallbackRef.current = func;
  }, [func]);

  useEffect(() => {
    return () => {
      debouncedStateRef.current?.cancel();
    };
  }, []);

  const debouncedState = useMemo(() => {
    debouncedStateRef.current?.cancel();

    const nextDebouncedState = createDebouncedFunction<T>(
      (...args) => latestCallbackRef.current(...args) as ReturnType<T>,
      delay,
      {
        leading: isLeading,
        maxWait,
        trailing: isTrailing,
      },
    );

    debouncedStateRef.current = nextDebouncedState;
    return nextDebouncedState;
  }, [delay, isLeading, isTrailing, maxWait]);

  return useMemo(() => {
    const wrapped = ((...args: Parameters<T>) =>
      debouncedState.debounced(...args)) as DebouncedState<T>;

    wrapped.cancel = () => {
      debouncedState.cancel();
    };

    wrapped.flush = () => {
      return debouncedState.flush();
    };

    wrapped.isPending = () => {
      return debouncedState.isPending();
    };

    return wrapped;
  }, [debouncedState]);
}
